import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { listThread, sendMessage, markRead } from "../data/messages";
import { getTutorById } from "../data/tutors";
import { toast } from "./components/ToastHost";

const MAX_MESSAGE_LENGTH = 2000;

const timeFmt = (iso, lng) => {
  const ts = new Date(iso).getTime();
  if (!Number.isFinite(ts)) return "";
  return new Intl.DateTimeFormat(lng, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(ts));
};

function isValidPeerId(value) {
  return !!String(value || "").trim();
}

export default function Chat() {
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage || i18n.language || "en";

  const { peerId } = useParams();
  const [params] = useSearchParams();
  const peerNameFromLink = params.get("name") || "";

  const auth = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("auth") || "null");
    } catch {
      return null;
    }
  }, []);

  const [peer, setPeer] = useState({ id: peerId, name: "" });
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);
  const [pageError, setPageError] = useState("");

  const bottomRef = useRef(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      if (!peerId) return;

      if (String(auth?.role || "").toUpperCase() === "STUDENT") {
        try {
          const tutor = await getTutorById(peerId);
          const name =
            tutor?.profile?.displayName ||
            tutor?.name ||
            t("roles.tutor");

          if (alive) {
            setPeer({ id: peerId, name });
          }
          return;
        } catch {
          if (alive) {
            setPeer({ id: peerId, name: t("roles.tutor") });
          }
          return;
        }
      }

      if (alive) {
        setPeer({
          id: peerId,
          name: peerNameFromLink || t("roles.student"),
        });
      }
    })();

    return () => {
      alive = false;
    };
  }, [auth?.role, peerId, peerNameFromLink, t]);

  const refresh = useCallback(
    async (silent = false) => {
      if (!peerId) return;

      if (!silent) {
        setLoading(true);
        setPageError("");
      }
      if (silent) setRefreshing(true);

      try {
        const items = await listThread(peerId);
        const arr = Array.isArray(items) ? items : [];
        setMsgs(arr);

        const hasUnreadIncoming = arr.some(
          (m) => String(m.fromId) === String(peerId) && !m.readAt
        );

        if (hasUnreadIncoming) {
          try {
            await markRead(peerId);
          } catch {
            // silent
          }
        }
      } catch {
        if (!silent) {
          setPageError(t("chat.errors.load"));
        }
      } finally {
        if (!silent) setLoading(false);
        if (silent) setRefreshing(false);
      }
    },
    [peerId, t]
  );

  useEffect(() => {
    if (!auth || !peerId) return;
    refresh(false);
  }, [auth, peerId, refresh]);

  useEffect(() => {
    if (!auth || !peerId) return;

    const id = setInterval(() => {
      if (document.visibilityState === "visible") {
        refresh(true);
      }
    }, 3000);

    return () => clearInterval(id);
  }, [auth, peerId, refresh]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "auto" });
  }, [msgs.length]);

  const onSend = useCallback(async () => {
    const clean = text.trim();

    if (!clean || !peerId || sending) return;

    if (clean.length > MAX_MESSAGE_LENGTH) {
      toast(t("chat.errors.tooLong"), "error");
      return;
    }

    const tempId = `tmp_${Date.now()}_${Math.random()}`;
    const optimistic = {
      id: tempId,
      fromId: auth?.id,
      toId: peerId,
      text: clean,
      createdAt: new Date().toISOString(),
      _optimistic: true,
    };

    setMsgs((prev) => [...prev, optimistic]);
    setText("");
    setSending(true);

    try {
      await sendMessage({ toId: peerId, text: clean });
      await refresh(true);
    } catch (err) {
      setMsgs((prev) => prev.filter((m) => m.id !== tempId));
      if (err?.message === "CHAT_LOCKED") {
        toast(t("chat.errors.locked", { defaultValue: "Чат стане доступним після того, як спеціаліст прийме запит." }), "error");
      } else if (err?.message === "PROFANITY") {
        // Blocked by the backend profanity filter — put the text back into
        // the input so the user can rephrase instead of losing it.
        setText(clean);
        toast(t("chat.errors.profanity", { defaultValue: "Повідомлення містить недопустиму лексику. Будь ласка, перефразуйте." }), "error");
      } else {
        toast(t("chat.errors.send"), "error");
      }
    } finally {
      setSending(false);
    }
  }, [text, peerId, refresh, sending, auth?.id, t]);

  if (!auth) return <Navigate to="/login" replace />;

  if (!isValidPeerId(peerId)) {
    return (
      <main className="min-h-screen bg-[#272c39] text-white">
        <div className="mx-auto max-w-3xl px-4 pt-6 pb-28 md:pb-6">
          <div className="rounded-2xl bg-red-500/10 p-4 text-red-100 ring-1 ring-red-400/20">
            {t("chat.errors.invalidPeer")}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#272c39] text-white">
      <div className="mx-auto max-w-3xl px-4 pt-6 pb-28 md:pb-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-semibold">
              {peer?.name || t("chat.title")}
            </h1>

            {refreshing && (
              <span className="text-xs text-white/50">
                {t("common.updating")}
              </span>
            )}
          </div>

          <Link
            to="/messages"
            className="rounded-xl border border-white/20 px-3 py-1.5 hover:bg-white/10"
          >
            {t("chat.back")}
          </Link>
        </div>

        {pageError ? (
          <div className="mt-4 rounded-2xl bg-red-500/10 p-4 text-red-100 ring-1 ring-red-400/20">
            <div>{pageError}</div>
            <button
              onClick={() => refresh(false)}
              className="mt-3 rounded-xl border border-red-200/20 px-3 py-1.5 text-sm hover:bg-white/5"
            >
              {t("common.retry")}
            </button>
          </div>
        ) : null}

        <div className="mt-4 h-[60vh] overflow-y-auto rounded-2xl bg-white/5 p-4 ring-1 ring-white/10">
          {loading ? (
            <div className="grid h-full place-content-center text-white/60">
              {t("common.loading")}
            </div>
          ) : msgs.length === 0 ? (
            <div className="grid h-full place-content-center text-center text-white/60">
              <div>{t("chat.empty")}</div>
              <div className="mt-1 text-sm text-white/45">{t("chat.emptyHint")}</div>
            </div>
          ) : (
            <ul className="space-y-2">
              {msgs.map((m) => {
                const mine = String(m.fromId) === String(auth.id);
                const ts = m.createdAt || m.at;

                return (
                  <li key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                        mine ? "bg-[#7269e3]" : "bg-white/10"
                      } ${m._optimistic ? "opacity-80" : ""}`}
                    >
                      <p className="whitespace-pre-wrap break-words">{m.text}</p>
                      <div className="mt-1 text-right text-[10px] opacity-70">
                        {timeFmt(ts, lng)}
                      </div>
                    </div>
                  </li>
                );
              })}
              <div ref={bottomRef} />
            </ul>
          )}
        </div>

        <div className="mt-3 flex gap-2">
          <textarea
            rows={2}
            value={text}
            maxLength={MAX_MESSAGE_LENGTH}
            disabled={sending}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
            placeholder={t("chat.placeholder")}
            className="w-full resize-y rounded-xl border border-white/10 bg-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#7269e3] disabled:opacity-60"
          />

          <button
            onClick={onSend}
            disabled={sending || !text.trim()}
            className="shrink-0 self-end rounded-xl bg-[#7269e3] px-4 py-2 font-semibold hover:brightness-110 disabled:opacity-50"
          >
            {sending ? t("chat.sending") : t("chat.send")}
          </button>
        </div>

        <div className="mt-2 flex justify-between text-xs text-white/45">
          <span>{t("chat.enterHint")}</span>
          <span>
            {text.length}/{MAX_MESSAGE_LENGTH}
          </span>
        </div>
      </div>
    </main>
  );
}